// iranUsa.js — Scenario 2: the June 2025 US–Iran strikes ("Operation Midnight Hammer") and
// Iran's retaliatory missile strike on Al Udeid Air Base, Qatar.
//
// Illustrative OSINT-style reconstruction for demoing MapEditorModal's multidate capability —
// approximate dates/positions drawn from widely-reported open coverage (DoD press briefings,
// Reuters/AP wire reporting) of the June 2025 Israel–Iran war's US intervention. NOT derived
// from classified or precise geolocated sourcing — see .scratch/map-editor-demo/PLAN.md.

import { buildScenarioDoc, makeLayer, makeTrack, dtg } from '../buildScenarioDoc';
import { sidc, AFFILIATION, SYMBOL_SET, TACTICAL_SIDC } from '../sidc';

const US_AIR = sidc(AFFILIATION.FRIEND, SYMBOL_SET.AIR);
const US_SEA = sidc(AFFILIATION.FRIEND, SYMBOL_SET.SEA_SURFACE);
const US_INSTALLATION = sidc(AFFILIATION.FRIEND, SYMBOL_SET.LAND_INSTALLATION);
const IRAN_INSTALLATION = sidc(AFFILIATION.HOSTILE, SYMBOL_SET.LAND_INSTALLATION);
const IRAN_MISSILE = sidc(AFFILIATION.HOSTILE, SYMBOL_SET.LAND_EQUIPMENT);

const T = {
    israelStrikesBegin: dtg(2025, 6, 13),
    b2Departs: dtg(2025, 6, 21, 18),
    strikePackage: dtg(2025, 6, 22, 0, 30),
    strikeComplete: dtg(2025, 6, 22, 2),
    iranRetaliates: dtg(2025, 6, 23, 16),
    ceasefire: dtg(2025, 6, 24),
};

export const META = {
    id: 'iran-usa',
    title: 'US–Iran Strikes — "Operation Midnight Hammer" (June 2025)',
    summary: 'B-2 sorties from CONUS strike three Iranian nuclear sites; Iran retaliates against Al Udeid AB, Qatar; ceasefire follows within 48 hours.',
    dtgStartMs: T.israelStrikesBegin,
    dtgEndMs: T.ceasefire,
    initialDtgMs: T.strikeComplete,
    center: [52.5, 30.5],
    zoom: 5,
};

const layerUsAssets = makeLayer({ label: 'US Forces' });
const layerIranSites = makeLayer({ label: 'Iranian Nuclear Sites' });
const layerIranStrike = makeLayer({ label: 'Iranian Retaliation' });
const layerGraphics = makeLayer({ label: 'Strike Packages' });

const tracks = [
    // -- B-2 strike package: CONUS -> transit -> over target -> RTB ------------------------
    makeTrack({
        layerId: layerUsAssets.layerId,
        elementType: 'unit-icon',
        mobility: 'moving',
        snapshots: [
            [T.b2Departs, { type: 'Point', coordinates: [-93.55, 38.73] }, {
                sidc: US_AIR, label: 'B-2 Spirit strike package — Whiteman AFB, MO (wheels-up)',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: '509th Bomb Wing', AS_COUNTRY: 'USA' },
            }],
            [T.strikePackage, { type: 'Point', coordinates: [51.0, 34.9] }, {
                sidc: US_AIR, label: 'B-2 Spirit strike package — over Fordow',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: '509th Bomb Wing', AS_COUNTRY: 'USA' },
            }],
            [T.strikeComplete, { type: 'Point', coordinates: [50.0, 37.0] }, {
                sidc: US_AIR, label: 'B-2 Spirit strike package — egress',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: '509th Bomb Wing', AS_COUNTRY: 'USA' },
            }],
        ],
    }),
    // -- US carrier strike group on station in the Arabian Sea / Gulf of Oman ---------------
    makeTrack({
        layerId: layerUsAssets.layerId,
        elementType: 'unit-icon',
        mobility: 'moving',
        snapshots: [
            [T.israelStrikesBegin, { type: 'Point', coordinates: [58.0, 24.5] }, {
                sidc: US_SEA, label: 'USS Nimitz CSG — Arabian Sea',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'Nimitz CSG', AS_COUNTRY: 'USA' },
            }],
            [T.strikeComplete, { type: 'Point', coordinates: [57.0, 25.2] }, {
                sidc: US_SEA, label: 'USS Nimitz CSG — repositioned closer to Strait of Hormuz',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'Nimitz CSG', AS_COUNTRY: 'USA' },
            }],
            [T.iranRetaliates, { type: 'Point', coordinates: [56.4, 25.6] }, {
                sidc: US_SEA, label: 'USS Nimitz CSG — heightened air-defense posture',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'Nimitz CSG', AS_COUNTRY: 'USA' },
            }],
        ],
    }),
    // -- Al Udeid Air Base, Qatar — struck, then back to normal ops after ceasefire ---------
    makeTrack({
        layerId: layerUsAssets.layerId,
        elementType: 'unit-icon',
        mobility: 'stationary',
        snapshots: [
            [T.israelStrikesBegin, { type: 'Point', coordinates: [51.316, 25.117] }, {
                sidc: US_INSTALLATION, label: 'Al Udeid Air Base — normal operations',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'Al Udeid AB', AS_COUNTRY: 'QAT/USA' },
            }],
            [T.iranRetaliates, { type: 'Point', coordinates: [51.316, 25.117] }, {
                sidc: US_INSTALLATION, label: 'Al Udeid Air Base — under missile attack (intercepted)',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'Al Udeid AB', AS_COUNTRY: 'QAT/USA', AL_OPERATIONAL_CONDITION: 'DAMAGED' },
            }],
            [T.ceasefire, { type: 'Point', coordinates: [51.316, 25.117] }, {
                sidc: US_INSTALLATION, label: 'Al Udeid Air Base — resumed operations, ceasefire holding',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'Al Udeid AB', AS_COUNTRY: 'QAT/USA', AL_OPERATIONAL_CONDITION: 'FMC' },
            }],
        ],
    }),
    // -- Three Iranian nuclear sites: status flips from active to struck/damaged -------------
    makeTrack({
        layerId: layerIranSites.layerId,
        elementType: 'unit-icon',
        mobility: 'stationary',
        snapshots: [
            [T.israelStrikesBegin, { type: 'Point', coordinates: [50.9962, 34.8862] }, {
                sidc: IRAN_INSTALLATION, label: 'Fordow Fuel Enrichment Plant — active',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'Fordow FEP', AS_COUNTRY: 'IRN' },
            }],
            [T.strikeComplete, { type: 'Point', coordinates: [50.9962, 34.8862] }, {
                sidc: IRAN_INSTALLATION, label: 'Fordow Fuel Enrichment Plant — struck (GBU-57 MOP x12)',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'Fordow FEP', AS_COUNTRY: 'IRN', AL_OPERATIONAL_CONDITION: 'DAMAGED' },
            }],
        ],
    }),
    makeTrack({
        layerId: layerIranSites.layerId,
        elementType: 'unit-icon',
        mobility: 'stationary',
        snapshots: [
            [T.israelStrikesBegin, { type: 'Point', coordinates: [51.7268, 33.7248] }, {
                sidc: IRAN_INSTALLATION, label: 'Natanz Enrichment Complex — active (already Israeli-struck 6/13)',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'Natanz', AS_COUNTRY: 'IRN', AL_OPERATIONAL_CONDITION: 'DAMAGED' },
            }],
            [T.strikeComplete, { type: 'Point', coordinates: [51.7268, 33.7248] }, {
                sidc: IRAN_INSTALLATION, label: 'Natanz Enrichment Complex — struck (cruise missiles)',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'Natanz', AS_COUNTRY: 'IRN', AL_OPERATIONAL_CONDITION: 'NMC' },
            }],
        ],
    }),
    makeTrack({
        layerId: layerIranSites.layerId,
        elementType: 'unit-icon',
        mobility: 'stationary',
        snapshots: [
            [T.israelStrikesBegin, { type: 'Point', coordinates: [51.6746, 32.6546] }, {
                sidc: IRAN_INSTALLATION, label: 'Isfahan Nuclear Technology Center — active',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'Isfahan NTC', AS_COUNTRY: 'IRN' },
            }],
            [T.strikeComplete, { type: 'Point', coordinates: [51.6746, 32.6546] }, {
                sidc: IRAN_INSTALLATION, label: 'Isfahan Nuclear Technology Center — struck (Tomahawk SLCM)',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'Isfahan NTC', AS_COUNTRY: 'IRN', AL_OPERATIONAL_CONDITION: 'DAMAGED' },
            }],
        ],
    }),
    // -- Iranian missile battery: launch toward Al Udeid, then stood down after ceasefire ----
    makeTrack({
        layerId: layerIranStrike.layerId,
        elementType: 'unit-icon',
        mobility: 'moving',
        snapshots: [
            [T.strikeComplete, { type: 'Point', coordinates: [53.5, 29.5] }, {
                sidc: IRAN_MISSILE, label: 'IRGC-ASF Fateh/Emad missile battery — repositioning',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'IRGC-ASF battery', AS_COUNTRY: 'IRN' },
            }],
            [T.iranRetaliates, { type: 'Point', coordinates: [53.5, 29.5] }, {
                sidc: IRAN_MISSILE, label: 'IRGC-ASF missile battery — launched salvo at Al Udeid AB',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'IRGC-ASF battery', AS_COUNTRY: 'IRN' },
            }],
            [T.ceasefire, { type: 'Point', coordinates: [53.5, 29.5] }, {
                sidc: IRAN_MISSILE, label: 'IRGC-ASF missile battery — stood down, ceasefire',
                sidcModifiers: { T_UNIQUE_DESIGNATION_1: 'IRGC-ASF battery', AS_COUNTRY: 'IRN', AL_OPERATIONAL_CONDITION: 'FMC' },
            }],
        ],
    }),
    // -- Strike-package ingress axis: Whiteman AFB -> Fordow (single long-range sortie) -----
    makeTrack({
        layerId: layerGraphics.layerId,
        elementType: 'tactical-line',
        mobility: null,
        graphicType: 'axis-of-advance',
        snapshots: [
            [T.strikePackage, {
                type: 'LineString',
                coordinates: [[50.0, 37.0], [50.9, 35.2]],
            }, { sidc: TACTICAL_SIDC.DIRATK_AIR, label: 'B-2 ingress — Fordow' }],
        ],
    }),
];

export const doc = buildScenarioDoc({
    dtgStartMs: META.dtgStartMs,
    dtgEndMs: META.dtgEndMs,
    layers: [layerUsAssets, layerIranSites, layerIranStrike, layerGraphics],
    tracks,
    views: [{ name: 'Gulf theater', bbox: [[48.0, 23.5], [58.5, 36.5]] }],
});
